import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { translations } from '../data/mockData';
import { Button } from './ui/button';

// Support both REACT_APP_API_URL (new) and REACT_APP_BACKEND_URL (legacy)
const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const { language } = useLanguage();
  const t = translations[language].paymentSuccess;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const method = searchParams.get('method');
        const sessionId = searchParams.get('session_id');
        
        if (method === 'paypal') {
          // Handle PayPal return - capture the payment
          const orderId = localStorage.getItem('paypal_order_id');
          const paypalOrderId = localStorage.getItem('paypal_paypal_order_id');
          
          if (orderId && paypalOrderId) {
            const response = await fetch(`${API_URL}/api/checkout/paypal/capture`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paypal_order_id: paypalOrderId,
                order_id: orderId
              })
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
              setOrderDetails({ orderId: data.order_id });
              clearCart();
              // Clean up localStorage
              localStorage.removeItem('paypal_order_id');
              localStorage.removeItem('paypal_paypal_order_id');
            } else {
              setError('Payment verification failed');
            }
          }
        } else if (sessionId) {
          // Handle Stripe return
          const response = await fetch(`${API_URL}/api/checkout/stripe/status/${sessionId}`);
          const data = await response.json();
          
          if (data.payment_status === 'paid') {
            setOrderDetails({ 
              orderId: data.metadata?.order_id,
              amount: data.amount_total
            });
            clearCart();
          } else {
            setError('Payment not completed');
          }
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Failed to verify payment');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams, clearCart]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#141619] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">
            {language === 'es' ? 'Verificando pago...' : 
             language === 'de' ? 'Zahlung wird überprüft...' : 
             'Verifying payment...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#141619] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {language === 'es' ? 'Error de Verificación' : 
             language === 'de' ? 'Verifizierungsfehler' : 
             'Verification Error'}
          </h1>
          <p className="text-[#9CA3AF] mb-8">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619] px-8 py-3 rounded-full"
          >
            {t.backHome}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141619] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {/* Success Icon */}
        <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
          <CheckCircle className="w-14 h-14 text-green-500" />
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="payment-success-title">
          {t.title}
        </h1>

        {/* Message */}
        <p className="text-[#9CA3AF] text-lg mb-8">
          {t.message}
        </p>

        {/* Order Number */}
        {orderDetails?.orderId && (
          <div className="bg-[#1a1d21] rounded-xl p-6 border border-[#2a2d32] mb-8">
            <p className="text-[#9CA3AF] text-sm mb-2">{t.orderNumber}</p>
            <p className="text-[#D4AF37] font-mono text-lg font-bold">
              #{orderDetails.orderId.substring(0, 8).toUpperCase()}
            </p>
          </div>
        )}

        {/* Back to Home Button */}
        <Button
          onClick={() => navigate('/')}
          className="bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105"
          data-testid="back-home-btn"
        >
          {t.backHome}
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
